/**
 * pages/AdminOverview.jsx
 * -----------------------
 * Lean admin command-center built on the v2.71.27 framework. Sits
 * alongside the legacy /admin (715-LOC comprehensive tab UI which stays
 * UNTOUCHED for production safety).
 *
 * URL: /admin/overview
 *
 * Composition only — no new fetches, no new branding.
 *   • `<DashboardLayout>` shell with admin sidebar (9 deep-link items)
 *   • 4 StatCards top row: Listings · Pending · Users · Enquiries
 *   • 4-up QuickActions: Approvals · Add Property · Add Client · Plans
 *   • 4 rails: Pending Approvals · Recent Enquiries · Latest Listings ·
 *     Recent Users
 *
 * Data sources (all existing endpoints):
 *   • `/api/admin/overview` — aggregate stats
 *   • `/api/properties?approval_status=pending` — pending approvals rail
 *   • `/api/admin/enquiries` — recent enquiries (admin scope)
 *   • `/api/properties` (admin sees all incl. pending) — latest listings
 *   • `/api/admin/users` — recent signups
 *
 * v2.71.30 — Initial.
 */

import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  Building2, ClipboardList, Users, MessageCircle, Plus, UserPlus, Crown,
  ArrowRight, Eye, ShieldCheck, Star, Settings,
} from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { formatINR } from "../shared/services/api";
import api from "../lib/api";
import { useDataSync, useAutoRefresh } from "../lib/dataSync";
import {
  DashboardLayout, StatCard, QuickActions, RecentItemsList, Guard,
} from "../features/dashboard";

function ago(iso) {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (isNaN(t)) return "";
  const m = Math.floor((Date.now() - t) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

export default function AdminOverview() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [latest, setLatest] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // v2.72 — Loader extracted so dataSync + auto-refresh can re-fire it.
  // `cancelRef` guards against state updates after unmount.
  const cancelRef = React.useRef(false);
  const load = React.useCallback(() => {
    if (!user || !isAdmin) return;
    Promise.allSettled([
      api.get("/admin/overview"),
      api.get("/properties?approval_status=pending"),
      api.get("/admin/enquiries"),
      api.get("/properties"),
      api.get("/admin/users").catch(() => ({ data: { results: [] } })),
    ]).then((res) => {
      if (cancelRef.current) return;
      const [statsR, pendingR, enqR, listR, usersR] = res;
      if (statsR.status === "fulfilled") setStats(statsR.value.data);
      if (pendingR.status === "fulfilled") {
        // v2.71.99 — Hard-filter so approved/sold properties NEVER leak into
        // the Pending widget. Belt-and-braces — backend already returns only
        // pending, but the public /properties feed could be reused here later.
        const items = pendingR.value.data?.results || [];
        setPending(items.filter((p) => (p.approval_status || "").toLowerCase() === "pending"));
      }
      if (enqR.status === "fulfilled") {
        setEnquiries(enqR.value.data?.results || enqR.value.data || []);
      }
      if (listR.status === "fulfilled") {
        const items = listR.value.data?.results || [];
        // v2.71.99 — Sort newest first by created_at DESC (defence in depth —
        // backend already sorts but the client-side guarantee makes the rail
        // resilient if a future endpoint forgets to sort).
        const sorted = [...items].sort((a, b) => {
          const ta = new Date(a.created_at || 0).getTime();
          const tb = new Date(b.created_at || 0).getTime();
          return tb - ta;
        });
        setLatest(sorted.slice(0, 5));
      }
      if (usersR.status === "fulfilled") {
        const u = usersR.value.data?.results || usersR.value.data?.users || usersR.value.data || [];
        const arr = Array.isArray(u) ? u : [];
        // Newest registrations on top
        const sorted = [...arr].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        setUsers(sorted.slice(0, 5));
      }
      setLoading(false);
    });
  }, [user, isAdmin]);

  useEffect(() => {
    cancelRef.current = false;
    load();
    return () => { cancelRef.current = true; };
  }, [load]);

  // v2.72 — Command-centre counters/rails refresh instantly after any
  // mutation anywhere + every 45 s while the tab is visible. No stale values.
  useDataSync(["properties", "approvals", "users", "leads", "enquiries", "projects", "dashboard"], load);
  useAutoRefresh(load, 45000);

  if (authLoading) return <DashboardLayout title="Admin Overview" sidebar />;
  if (!user) return <Navigate to="/login?next=/admin/overview" replace />;
  if (!isAdmin) return <Navigate to="/account" replace />;

  const props = stats?.properties || {};
  const usrs = stats?.users || {};
  const enq = stats?.enquiries || {};

  // v2.90 — Public/admin listing count reconciliation.
  // Public list endpoint returns `approval_status=approved` docs only. Admin
  // count is total. The diff (pending + draft + rejected) is what public
  // viewers can't see — surface it so admins understand why the marketplace
  // count on the site is lower than the total listings count here.
  const totalListings = props.total ?? 0;
  const approvedListings = props.approved ?? 0;
  const hiddenFromPublic = Math.max(0, totalListings - approvedListings);
  const listingsHint = loading
    ? "…"
    : hiddenFromPublic > 0
    ? `${approvedListings} live · ${hiddenFromPublic} hidden (${props.pending ?? 0} pending · ${props.draft ?? 0} draft · ${props.rejected ?? 0} rejected)`
    : `${props.sold_total ?? 0} sold · ${props.premium ?? 0} premium`;

  return (
    <DashboardLayout
      title="Admin Overview"
      subtitle="Quick command-centre across the platform."
      actions={
        <>
          <Link
            to="/admin"
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-[#E4E4E7] hover:border-[#27272A] text-[#27272A] text-[13px] font-medium rounded-sm transition-colors"
            data-testid="admin-full-console-link"
          >
            <Settings size={14} /> Full Console
          </Link>
          <Guard perm="admin:*">
            <Link
              to="/admin/listings?add=1"
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#0055FF] text-white text-[13px] font-semibold rounded-sm hover:bg-[#0044CC] transition-colors"
              data-testid="admin-add-property-cta"
            >
              <Plus size={14} /> Add Property
            </Link>
          </Guard>
        </>
      }
    >
      {/* ── Stats: 2×2 mobile, 4×1 desktop ─────────────────────────────── */}
      <section className="mb-5" data-testid="admin-overview">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard
            label="Listings"
            icon={Building2}
            value={loading ? "…" : props.total ?? "—"}
            hint={listingsHint}
            testid="admin-stat-listings"
          />
          <StatCard
            label="Pending"
            icon={ClipboardList}
            value={loading ? "…" : props.pending ?? 0}
            hint="Awaiting approval"
            testid="admin-stat-pending"
          />
          <StatCard
            label="Users"
            icon={Users}
            value={loading ? "…" : usrs.total ?? "—"}
            hint={`${usrs.dealers ?? 0} dealer · ${usrs.clients ?? 0} client`}
            testid="admin-stat-users"
          />
          <StatCard
            label="Enquiries"
            icon={MessageCircle}
            value={loading ? "…" : enq.total ?? 0}
            hint={`${enq.new ?? 0} new`}
            testid="admin-stat-enquiries"
          />
        </div>
      </section>

      {/* ── Quick Actions (desktop only — mobile uses MobileQuickActions
          pill row injected by DashboardLayout via QUICK_ACTIONS_CONFIG) ── */}
      <section className="mb-5 hidden md:block">
        <QuickActions
          actions={[
            { label: "Approvals", to: "/admin/approvals", icon: ClipboardList },
            { label: "Add Property", to: "/admin/listings?add=1", icon: Plus },
            { label: "Add Client", to: "/admin/add-client", icon: UserPlus },
            { label: "Plans", to: "/admin/plans", icon: Crown },
          ]}
        />
      </section>

      {/* ── Rails ─────────────────────────────────────────────────────── */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5">
        {/* Pending Approvals — most actionable rail; sits top-left */}
        <RecentItemsList
          title={`Pending Approvals${pending.length ? ` (${pending.length})` : ""}`}
          items={loading ? [] : pending.slice(0, 5)}
          emptyHint={loading ? "Loading…" : "Nothing pending. Inbox zero."}
          viewAllTo={pending.length > 5 ? "/admin/approvals" : undefined}
          testid="admin-rail-pending"
          renderItem={(p) => (
            <Link
              to={`/admin/approvals?id=${p.id}`}
              className="flex items-center gap-3 group"
              data-testid={`admin-pending-row-${p.id}`}
            >
              {p.images?.[0] ? (
                <img
                  src={p.images[0]} alt=""
                  loading="lazy" decoding="async" width="56" height="42"
                  className="w-14 h-[42px] object-cover rounded-sm bg-[#F4F4F5] flex-shrink-0"
                />
              ) : (
                <div className="w-14 h-[42px] bg-[#F4F4F5] rounded-sm flex-shrink-0 flex items-center justify-center text-[#A1A1AA]">
                  <Building2 size={16} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-[#09090B] truncate group-hover:text-[#0055FF]">
                  {p.title || "Untitled"}
                </div>
                <div className="text-[11px] text-[#71717A] mt-0.5 flex items-center gap-1.5 truncate">
                  <span className="px-1.5 py-0.5 bg-[#FEF3C7] text-[#92400E] rounded-sm text-[10px] font-semibold uppercase tracking-wider">REVIEW</span>
                  {p.main_area ? <span>{p.main_area}</span> : null}
                  {p.price ? <span className="font-semibold text-[#27272A]">· {formatINR(p.price)}</span> : null}
                </div>
              </div>
              <ArrowRight size={14} className="text-[#A1A1AA] group-hover:text-[#0055FF] flex-shrink-0" />
            </Link>
          )}
        />

        {/* Recent Enquiries */}
        <RecentItemsList
          title={`Recent Enquiries${enquiries.length ? ` (${enquiries.length})` : ""}`}
          items={loading ? [] : enquiries.slice(0, 5)}
          emptyHint={loading ? "Loading…" : "No enquiries yet."}
          viewAllTo={enquiries.length > 5 ? "/admin?tab=enquiries" : undefined}
          testid="admin-rail-enquiries"
          renderItem={(e) => (
            <Link
              to={`/property/${e.property_id}`}
              className="flex items-center gap-3 group"
              data-testid={`admin-enquiry-row-${e.id}`}
            >
              <div className="w-10 h-10 bg-[#EEF4FF] rounded-full flex-shrink-0 flex items-center justify-center text-[#0055FF] font-semibold text-[12px]">
                {(e.name || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-[13px] font-medium text-[#09090B] truncate">{e.name || "Anonymous"}</span>
                  {e.kind === "callback_request" && (
                    <span className="px-1 py-0 bg-[#FEF3C7] text-[#92400E] rounded-sm text-[9px] font-semibold uppercase tracking-wider">CB</span>
                  )}
                </div>
                <div className="text-[11px] text-[#71717A] mt-0.5 truncate">
                  {e.property_title || "Property"} · {ago(e.created_at)}
                </div>
              </div>
              <span className={`text-[10px] font-semibold uppercase tracking-wider flex-shrink-0 ${
                e.status === "closed" ? "text-[#16A34A]"
                : e.status === "contacted" ? "text-[#0055FF]"
                : "text-[#92400E]"
              }`}>
                {e.status === "new" ? "NEW" : (e.status || "new").toUpperCase()}
              </span>
            </Link>
          )}
        />

        {/* Latest Listings — v2.71.99: explicit columns + View All link */}
        <RecentItemsList
          title="Latest Listings"
          items={loading ? [] : latest}
          emptyHint={loading ? "Loading…" : "No properties yet."}
          viewAllTo="/admin/listings"
          testid="admin-rail-latest"
          renderItem={(p) => (
            <Link
              to={`/property/${p.id}`}
              className="flex items-center gap-3 group"
              data-testid={`admin-latest-row-${p.id}`}
            >
              {p.images?.[0] ? (
                <img
                  src={p.images[0]} alt=""
                  loading="lazy" decoding="async" width="56" height="42"
                  className="w-14 h-[42px] object-cover rounded-sm bg-[#F4F4F5] flex-shrink-0"
                />
              ) : (
                <div className="w-14 h-[42px] bg-[#F4F4F5] rounded-sm flex-shrink-0 flex items-center justify-center text-[#A1A1AA]">
                  <Building2 size={16} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-[13px] font-medium text-[#09090B] truncate group-hover:text-[#0055FF]">
                    {p.title || "Untitled"}
                  </span>
                  {p.featured ? <Star size={11} className="text-[#F59E0B] flex-shrink-0 fill-current" /> : null}
                </div>
                <div className="text-[11px] text-[#71717A] mt-0.5 flex items-center gap-1.5 flex-wrap">
                  {/* Status badge */}
                  {(["Sold","Rented"].includes(p.status)) ? (
                    <span className="px-1.5 py-0.5 bg-[#DBEAFE] text-[#1D4ED8] rounded-sm text-[10px] font-semibold uppercase tracking-wider">SOLD</span>
                  ) : p.approval_status === "approved" ? (
                    <span className="px-1.5 py-0.5 bg-[#DCFCE7] text-[#15803D] rounded-sm text-[10px] font-semibold uppercase tracking-wider">LIVE</span>
                  ) : p.approval_status === "pending" ? (
                    <span className="px-1.5 py-0.5 bg-[#FEF3C7] text-[#92400E] rounded-sm text-[10px] font-semibold uppercase tracking-wider">REVIEW</span>
                  ) : null}
                  {p.price ? <span className="font-semibold text-[#27272A]">{formatINR(p.price)}</span> : null}
                  {(p.area_value && p.area_unit) ? <span>· {p.area_value} {p.area_unit}</span> : null}
                  {p.main_area ? <span>· {p.main_area}</span> : null}
                  <span>· {ago(p.created_at)}</span>
                </div>
              </div>
              <ArrowRight size={14} className="text-[#A1A1AA] group-hover:text-[#0055FF] flex-shrink-0" />
            </Link>
          )}
        />

        {/* Recent Users — v2.71.99: rows are now Links to /admin/users/:id */}
        <RecentItemsList
          title="Recent Users"
          items={loading ? [] : users}
          emptyHint={loading ? "Loading…" : "No users yet."}
          viewAllTo="/admin/users"
          testid="admin-rail-users"
          renderItem={(u) => (
            <Link
              to={`/admin/users/${u.id}`}
              className="flex items-center gap-3 group"
              data-testid={`admin-user-row-${u.id}`}
            >
              <div className="w-10 h-10 bg-[#F4F4F5] rounded-full flex-shrink-0 flex items-center justify-center text-[#27272A] font-semibold text-[12px]">
                {(u.name || u.email || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-[#09090B] truncate group-hover:text-[#0055FF]">
                  {u.name || u.email || "Unnamed"}
                </div>
                <div className="text-[11px] text-[#71717A] mt-0.5 truncate">
                  <span className="uppercase tracking-wider font-semibold">{u.role || "client"}</span>
                  {u.created_at ? <span> · {ago(u.created_at)}</span> : null}
                </div>
              </div>
              {u.subscription?.tier ? (
                <span className="text-[10px] font-semibold text-[#0055FF] uppercase tracking-wider flex-shrink-0 inline-flex items-center gap-1">
                  <ShieldCheck size={11} /> {u.subscription.tier}
                </span>
              ) : null}
              <ArrowRight size={14} className="text-[#A1A1AA] group-hover:text-[#0055FF] flex-shrink-0" />
            </Link>
          )}
        />
      </section>

      {/* Footer link back to full console */}
      <div className="mt-6 text-[11px] text-[#A1A1AA] flex items-center gap-2" data-testid="admin-full-link">
        <Link to="/admin" className="inline-flex items-center gap-1 hover:text-[#27272A]">
          <Settings size={12} /> Full Admin Console
        </Link>
        <span>·</span>
        <Link to="/admin?tab=analytics" className="inline-flex items-center gap-1 hover:text-[#27272A]">
          <Eye size={12} /> Analytics
        </Link>
      </div>
    </DashboardLayout>
  );
}
